// generateCollectionPageJsonLd.js
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { DEFAULT_LANGUAGE } from '../resource/i18nCommon.js';
import { CategoryI18nKey } from '../resource/categoryConstants.js';
import { normalizeLang, getHreflang } from '../resource/langPath.js';

/**
 * Generate JSON-LD for category collection pages
 * Supports two modes:
 * 1. Category All: Multiple feeds with 2-level breadcrumb
 * 2. Category ListType: Single feed with 3-level breadcrumb
 *
 * @param {Object} params
 * @param {string} params.origin - URL origin (default: 'https://swag.live')
 * @param {string} params.categoryId - Category ID
 * @param {string} [params.listType] - Optional list type (flix/short/image). If provided, generates Category ListType mode
 * @param {Object} params.state - Redux state (Immutable.js)
 * @param {Object} params.i18n - i18n instance (required)
 * @returns {Object} JSON-LD object
 */
export const generateCollectionPageJsonLd = async ({
  origin = 'https://swag.live',
  categoryId,
  listType = null,
  i18n,
}) => {
  const { t } = i18n;
  // if not pass listType, it is Category All
  const isCategoryListType = !!listType;

  const currentLanguage = i18n.language;
  const langPath =
    normalizeLang(currentLanguage) === DEFAULT_LANGUAGE
      ? ''
      : `/${normalizeLang(currentLanguage)}`;
  const originWithLang = `${origin}${langPath}`;
  const normalizedInLanguage = getHreflang(currentLanguage);

  const loadLanguagePromise = async () => {
    if (!i18n.hasResourceBundle(currentLanguage, TranslationNamespace.SEO)) {
      await i18n.reloadResources(currentLanguage, TranslationNamespace.SEO);
    }
  };

  await loadLanguagePromise();

  const getCategoryNameByLang = lang =>
    t(`category_${categoryId}`, {
      ns: TranslationNamespace.FEED,
      lng: lang,
    });

  const getCategoryAllTitle = lang =>
    t('seo_category_all_title', {
      ns: TranslationNamespace.SEO,
      lng: lang,
      category: getCategoryNameByLang(lang),
    });

  const getCategoryPageTitle = lang => {
    if (!isCategoryListType) {
      return getCategoryAllTitle(lang);
    }
    const categoryListType = CategoryI18nKey[listType];
    return t(`seo_category_${categoryListType}_title`, {
      ns: TranslationNamespace.SEO,
      lng: lang,
      category: getCategoryNameByLang(lang),
    });
  };

  const getHomeTitle = lang =>
    t('seo-home-title', {
      ns: TranslationNamespace.SEO,
      lng: lang,
    });

  const pageTitle = getCategoryPageTitle(currentLanguage);

  const allCategoryTitle = getCategoryAllTitle(currentLanguage);

  const homeTitle = getHomeTitle(currentLanguage);

  // Build @graph array
  const graph = [];

  // 1. CollectionPage
  graph.push({
    '@type': 'CollectionPage',
    '@id': isCategoryListType
      ? `${originWithLang}/category/${categoryId}/${listType}#webpage`
      : `${originWithLang}/category/${categoryId}#webpage`,
    url: isCategoryListType
      ? `${originWithLang}/category/${categoryId}/${listType}`
      : `${originWithLang}/category/${categoryId}`,
    inLanguage: normalizedInLanguage,
    name: pageTitle,
    breadcrumb: {
      '@id': isCategoryListType
        ? `${originWithLang}/category/${categoryId}/${listType}#breadcrumb`
        : `${originWithLang}/category/${categoryId}#breadcrumb`,
    },
    mainEntity: {
      '@id': isCategoryListType
        ? `${originWithLang}/category/${categoryId}/${listType}#feed`
        : `${originWithLang}/category/${categoryId}#feed`,
    },
  });

  // 2. BreadcrumbList
  graph.push({
    '@type': 'BreadcrumbList',
    '@id': isCategoryListType
      ? `${originWithLang}/category/${categoryId}/${listType}#breadcrumb`
      : `${originWithLang}/category/${categoryId}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: homeTitle,
        item: {
          '@id': `${originWithLang}/#website`,
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: allCategoryTitle,
        item: {
          '@id': `${originWithLang}/category/${categoryId}#webpage`,
        },
      },
      ...(isCategoryListType
        ? [
            {
              '@type': 'ListItem',
              position: 3,
              name: pageTitle,
              item: {
                '@id': `${originWithLang}/category/${categoryId}/${listType}#webpage`,
              },
            },
          ]
        : []),
    ],
  });

  // 3 Map ItemList elements
  graph.push({
    '@type': 'ItemList',
    '@id': isCategoryListType
      ? `${originWithLang}/category/${categoryId}/${listType}#feed`
      : `${originWithLang}/category/${categoryId}#feed`,
    url: isCategoryListType
      ? `${originWithLang}/category/${categoryId}/${listType}`
      : `${originWithLang}/category/${categoryId}`,
  });

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
};

export default generateCollectionPageJsonLd;
